import Avatar from '@atlaskit/avatar';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';

import { getConfig } from '../../config';
import { redirect } from '../../facade/window';
import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import Store from '../../store';
import UIStore from '../../store/uiStore';
import UserStore from '../../store/userStore';
import { logout } from '../../util/auth';

const { atlassianAccountUrl } = getConfig();

interface Props {
  uiStore: UIStore;
  userStore: UserStore;
}

@observer
class UserMenu extends React.Component<Props & InjectedIntlProps & AnalyticsConsumerProps> {
  render() {
    const triggerButton = (
      <div data-test-id="user-menu">
        <Avatar borderColor="transparent" src={this.props.userStore.picture} size="small" />
      </div>
    );

    return (
      <DropdownMenu
        trigger={triggerButton}
        triggerType="default"
        shouldFlip={false}
        position={this.getTooltipPosition()}
      >
        <DropdownItemGroup>{this.createDropdownItems()}</DropdownItemGroup>
      </DropdownMenu>
    );
  }

  private createDropdownItems() {
    const manageProfileUrl = `${atlassianAccountUrl}/manage-profile`;

    const menuItems: { children: JSX.Element; href?: string; onClick?: (e: MouseEvent) => void }[] = [
      {
        children: (
          <FormattedMessage {...messages.manageYourAccount}>
            {msg => <span data-test-id="manage-account">{msg}</span>}
          </FormattedMessage>
        ),
        href: manageProfileUrl,
        onClick: async e => {
          e.preventDefault();
          await this.props.analytics.ui('manageProfileButton', 'clicked');
          redirect(manageProfileUrl);
        },
      },
      {
        children: (
          <FormattedMessage {...messages.logout}>{msg => <span data-test-id="logout">{msg}</span>}</FormattedMessage>
        ),
        onClick: async e => {
          e.preventDefault();
          await this.props.analytics.ui('logoutButton', 'clicked');
          logout();
        },
      },
    ];

    return menuItems.map((item, idx) => <DropdownItem key={`usermenu.dropdown.item.${idx}`} {...item} />);
  }

  private getTooltipPosition = () => (this.props.uiStore.mobileMode ? 'bottom right' : 'top left');
}

export default inject(({ userStore, uiStore }: Store) => ({
  uiStore,
  userStore,
}))(injectIntl(withAnalytics(UserMenu)));

const messages = defineMessages({
  logout: {
    defaultMessage: 'Log out',
    description: 'Link that logs out the current user',
    id: 'uchi.usermenu.logout-link',
  },
  manageYourAccount: {
    defaultMessage: 'Manage your account',
    description: `Link to update settings for a user's account`,
    id: 'uchi.usermenu.manage-account',
  },
});
